import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Chrome, Globe, Download, AlertTriangle } from "lucide-react";

export default function Extension() {
  const [connected, setConnected] = useState(true);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="extension-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Browser extension</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Web monitoring, in your browser.</h1>
      </div>

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {connected ? (
              <CheckCircle2 className="text-[#1D9E75]" size={20} />
            ) : (
              <AlertTriangle className="text-[#BA7517]" size={20} />
            )}
            <div>
              <p className="font-display font-bold text-[#1A1A2E]">
                {connected ? "Extension connected" : "Extension disconnected"}
              </p>
              <p className="text-xs text-[#555568]">
                {connected ? "Last heartbeat 12 seconds ago." : "Re-install the extension to reconnect."}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setConnected(!connected)}>
            {connected ? "Disconnect" : "Reconnect"}
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-3">Install</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="/wylo-extension.zip"
            download="wylo-extension.zip"
            data-testid="extension-download-chrome"
            className="rounded-md border border-[#E8ECF2] p-5 bg-[#F8F9FC] flex items-center gap-3 hover:bg-white transition-colors"
          >
            <Chrome className="text-[#1A3A5C]" />
            <div className="flex-1">
              <p className="font-display font-bold text-[#1A1A2E]">Chrome / Edge / Brave</p>
              <p className="text-xs text-[#555568]">Download the WYLO extension (v0.1)</p>
            </div>
            <Download size={16} className="text-[#555568]" />
          </a>
          <a
            href="/wylo-extension.zip"
            download="wylo-extension.zip"
            data-testid="extension-download-firefox"
            className="rounded-md border border-[#E8ECF2] p-5 bg-[#F8F9FC] flex items-center gap-3 hover:bg-white transition-colors"
          >
            <Globe className="text-[#1A3A5C]" />
            <div className="flex-1">
              <p className="font-display font-bold text-[#1A1A2E]">Firefox</p>
              <p className="text-xs text-[#555568]">Same package · MV3 with Firefox shim</p>
            </div>
            <Download size={16} className="text-[#555568]" />
          </a>
        </div>
        <details className="mt-4 text-xs text-[#555568]">
          <summary className="cursor-pointer text-[#2C6BAC] font-semibold">Install instructions</summary>
          <ol className="mt-2 list-decimal pl-5 space-y-1">
            <li>Unzip <code className="font-mono">wylo-extension.zip</code> somewhere persistent.</li>
            <li><strong>Chrome:</strong> open <code className="font-mono">chrome://extensions</code> → enable Developer mode → Load unpacked → pick the folder.</li>
            <li><strong>Firefox:</strong> open <code className="font-mono">about:debugging#/runtime/this-firefox</code> → Load Temporary Add-on → pick <code className="font-mono">manifest.json</code>.</li>
            <li>Open the WYLO popup → Options → paste your user ID and bearer token (we'll surface these from Settings → Browser extension shortly).</li>
          </ol>
        </details>
      </section>

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-3">Web-only FACE log</h2>
        <p className="text-sm text-[#555568] mb-4">FACE events detected via the browser extension. Data is shared with the same `/api/face/event` endpoint as the OS agent.</p>
        <ul className="text-sm space-y-2">
          {[
            ["X.com", "distraction", "11m", "12:42"],
            ["youtube.com", "distraction", "7m", "11:08"],
            ["mail.google.com", "interruption", "2m", "10:31"],
            ["linkedin.com", "distraction", "5m", "09:58"],
          ].map(([site, type, dur, time], i) => (
            <li key={i} className="flex items-center justify-between rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-3">
              <div className="flex items-center gap-3">
                <span className={`h-2 w-2 rounded-full ${type === "interruption" ? "bg-[#E24B4A]" : "bg-[#BA7517]"}`} />
                <p className="text-sm text-[#1A1A2E] font-mono">{site}</p>
              </div>
              <p className="text-xs text-[#555568] font-mono">{dur} · {time}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
